import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Send, Loader2, Paperclip, User, Calendar,
  Mail, Tag, Shield, CheckCircle, Clock
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import { confirmToast } from '@/utils/confirmToast';
import Sidebar from '@/components/Sidebar';
import TicketAttachmentUpload from '@/components/TicketAttachmentUpload';

const STATUS_COLORS = {
  'open': 'bg-yellow-100 text-yellow-700',
  'in_progress': 'bg-blue-100 text-blue-700',
  'resolved': 'bg-green-100 text-green-700'
};

export default function TicketDetailPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);

  useEffect(() => {
    fetchUser();
    fetchTicket();
  }, [ticketId]);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await fetch(`${API}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setUser(await response.json());
        }
      } catch (error) {
        console.error(error);
      }
    }
    setLoading(false);
  };

  const fetchTicket = async () => {
    const token = localStorage.getItem('token');
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/help-tickets/${ticketId}`, { headers });
      if (response.ok) {
        setTicket(await response.json());
      } else {
        toast.error('Ticket not found');
        navigate(user?.is_super_admin ? '/admin' : '/dashboard');
      }
    } catch (error) {
      toast.error('Failed to load ticket');
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;
    setSending(true);
    const token = localStorage.getItem('token');
    
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      
      const response = await fetch(`${API}/help-tickets/${ticketId}/reply`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          ticket_id: ticketId, 
          message: replyMessage, 
          attachment_url: attachment?.url || null 
        })
      });
      
      if (response.ok) {
        toast.success('Reply sent');
        setReplyMessage('');
        setAttachment(null);
        fetchTicket();
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleCloseTicket = () => {
    confirmToast(
      'Are you sure you want to close this ticket? This action marks the ticket as complete.',
      async () => {
        const token = localStorage.getItem('token');
        try {
          const response = await fetch(`${API}/help-tickets/${ticketId}/close`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            toast.success('Ticket closed successfully');
            fetchTicket();
          } else {
            const error = await response.json();
            toast.error(error.detail || 'Failed to close ticket');
          }
        } catch (error) {
          toast.error('Failed to close ticket');
        }
      },
      { title: 'Close Ticket' }
    );
  };

  const getTimeSince = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  const isAdmin = user?.is_super_admin;

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {user && <Sidebar user={user} />}
      <div className={`flex-1 flex flex-col ${user ? 'lg:ml-64' : ''}`}>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />Back
              </Button>
              {ticket?.status !== 'closed' && (
                <Button
                  onClick={handleCloseTicket}
                  variant="outline"
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Close Ticket
                </Button>
              )}
            </div>

            <div className="bg-white rounded-2xl border p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <h1 className="text-2xl font-bold">{ticket?.subject}</h1>
                {ticket?.status && (
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[ticket.status]}`}>
                    {ticket.status.replace('_', ' ').toUpperCase()}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-4">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4"/>
                  {ticket?.user_name || ticket?.user_email}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4"/>
                  Created {ticket?.created_at && getTimeSince(ticket.created_at)}
                </span>
                {ticket?.last_reply_at && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4"/>
                    Last activity {getTimeSince(ticket.last_reply_at)}
                  </span>
                )}
              </div>

              <p className="text-slate-700 whitespace-pre-wrap">{ticket?.description}</p>
              
              {ticket?.screenshot_url && (
                <div className="mt-4">
                  <img 
                    src={ticket.screenshot_url} 
                    alt="Ticket screenshot" 
                    className="max-w-md rounded-lg border"
                  />
                </div>
              )}
            </div>

            {/* Replies */}
            {ticket?.replies?.map(reply => (
              <div key={reply.reply_id} className={`p-4 rounded-xl mb-3 ${reply.is_admin ? 'bg-violet-50 border border-violet-200' : 'bg-slate-50 border'}`}>
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">
                    {reply.author_name || (reply.is_admin ? 'Admin' : 'User')}
                    {reply.is_admin && <Shield className="w-4 h-4 inline ml-1 text-violet-600"/>}
                  </span>
                  <span className="text-xs text-slate-500">{new Date(reply.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm">{reply.message}</p>
                {reply.attachment_url && (
                  <div className="mt-2">
                    <a 
                      href={reply.attachment_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-violet-600 hover:underline flex items-center gap-1"
                    >
                      <Paperclip className="w-3 h-3" />
                      View attachment
                    </a>
                  </div>
                )}
              </div>
            ))}

            {/* Reply Form */}
            {ticket?.status !== 'closed' && (
              <div className="bg-white rounded-2xl border p-6">
                <Label className="mb-2">Your Reply</Label>
                <Textarea 
                  value={replyMessage} 
                  onChange={(e) => setReplyMessage(e.target.value)} 
                  placeholder="Type your reply..." 
                  className="mb-3" 
                  rows={4}
                />
                <TicketAttachmentUpload onUploadComplete={setAttachment} />
                <Button 
                  onClick={handleSendReply} 
                  disabled={sending || !replyMessage.trim()} 
                  className="bg-violet-600 text-white mt-3"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2"/>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2"/>
                      Send Reply
                    </>
                  )}
                </Button>
              </div>
            )}

            {ticket?.status === 'closed' && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-green-700 font-medium">This ticket has been closed</p>
                <p className="text-sm text-green-600 mt-1">No further replies can be added</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
